// Estado — OwnProfileView's status list, custom status (CustomStatusEditorSheet), out of office
// (OutOfOfficeEditorSheet) and the administrators' presence pin, each optimistic with the server's
// truth restored on refusal (OwnProfileViewModel). Web extras from the Mattermost webapp: how long
// "No molestar" lasts, any emoji for the custom status, and the recent custom statuses.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import type { RecentCustomStatus } from '../../../data/features/settings';
import { dndDurations, dndEndTime, formatExpiry, type DndDuration } from '../../../core/accountSettings';
import { CustomStatusClearAfter, CustomStatusCodec, CustomStatusDurations, customStatusClearAfterAllCases, fallbackCharacter, type CustomStatus } from '../../../core/customStatus';
import { PresenceOverrides, type PresenceOverride } from '../../../core/presenceOverride';
import { Icon } from '../../../design/icons';
import { locale, t, tf } from '../../../i18n/i18n';
import { useLive } from '../../hooks';
import { Button, Dialog } from '../../kit/Kit';
import { EmojiGlyph } from '../emoji/EmojiGlyph';
import { EmojiPicker } from '../emoji/EmojiPicker';
import { takeIntent } from './intent';
import { DndCustomDialog, dndUntilText } from './DndCustom';
import { PresenceDot, RadioRow, SettingsGroup, SwitchRow, useAccount, useSaver } from './parts';

const SELECTABLE = ['online', 'away', 'dnd', 'offline'] as const;

export function statusLabel(value: string): string {
  switch (value) {
    case 'online':
      return t('profile.status.online');
    case 'away':
      return t('profile.status.away');
    case 'dnd':
      return t('profile.status.dnd');
    case 'ooo':
      return t('profile.status.ooo');
    default:
      return t('profile.status.offline');
  }
}

export function useHour12(): boolean | undefined {
  const { prefs } = useAccount();
  const v = prefs?.display.use_military_time;
  // Unset = the apps' 24-hour clock (core/clockStyle, as Pantalla's switch shows it); only an
  // explicit "false" is 12-hour.
  return v === 'false';
}

export function clearAfterLabel(option: CustomStatusClearAfter): string {
  switch (option) {
    case CustomStatusClearAfter.dontClear:
      return t('profile.customStatus.dontClear');
    case CustomStatusClearAfter.thirtyMinutes:
      return t('profile.customStatus.30m');
    case CustomStatusClearAfter.oneHour:
      return t('profile.customStatus.1h');
    case CustomStatusClearAfter.fourHours:
      return t('profile.customStatus.4h');
    case CustomStatusClearAfter.today:
      return t('profile.customStatus.today');
    case CustomStatusClearAfter.thisWeek:
      return t('profile.customStatus.week');
  }
}

/** The DND submenu (webapp): until cleared, 30 min, 1 h, 2 h, tomorrow 9:00. */
export function DndDurationMenu({ anchor, onPick, onCustom, onClose }: { anchor: DOMRect; onPick(d: DndDuration): void; onCustom?(): void; onClose(): void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const id = setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  const width = 240;
  const left = Math.max(8, Math.min(anchor.right - width, window.innerWidth - width - 8));
  const below = anchor.bottom + 4 + 230 < window.innerHeight;
  return createPortal(
    <div ref={ref} className="popover st-dnd-menu" role="menu" style={{ position: 'fixed', left, width, ...(below ? { top: anchor.bottom + 4 } : { bottom: window.innerHeight - anchor.top + 4 }) }}>
      <div className="menu-label">{t('web.settings.status.dndFor')}</div>
      {dndDurations.map((d) => (
        <button key={d} type="button" className="menu-item" role="menuitem" onClick={() => onPick(d)}>
          {t(`web.settings.dnd.${d}`)}
        </button>
      ))}
      {onCustom ? (
        <button type="button" className="menu-item" role="menuitem" onClick={onCustom}>
          {t('web.dnd.custom')}
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

export function StatusSection() {
  const { account, patch } = useAccount();
  const hour12 = useHour12();
  const { value: local } = useLive(() => data.call('accountLocal'), ['users', 'userStatuses'], []);
  const status = local?.status ?? account?.status ?? 'offline';
  const now = Date.now();
  const custom: CustomStatus | null = useMemo(() => {
    const cs = CustomStatusCodec.fromPropsJSON(local?.user?.customStatusJSON ?? '');
    return cs && CustomStatusCodec.isActive(cs, now) ? cs : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local?.user?.customStatusJSON]);

  // MARK: presence
  const presence = useSaver();
  const [dndAnchor, setDndAnchor] = useState<DOMRect | null>(null);
  const [dndCustom, setDndCustom] = useState(false);
  const [dndEnd, setDndEnd] = useState(0);
  useEffect(() => setDndEnd(account?.dndEndTime ?? 0), [account?.dndEndTime]);
  const setStatus = (value: string, end = 0) => {
    const before = dndEnd;
    setDndEnd(value === 'dnd' ? end : 0);
    presence.run(
      async () => {
        const ok = await data.call('accountSetStatus', value, end);
        // Leaving ooo deactivates the auto-responder inside the worker — the switch below says so.
        if (ok && account?.autoResponder.active && value !== 'ooo') patch({ autoResponder: { ...account.autoResponder, active: false } });
        return ok;
      },
      () => setDndEnd(before),
      t('profile.error.statusUpdate'),
    );
  };

  // MARK: custom status
  const customSaver = useSaver();
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (takeIntent('customStatus')) setEditing(true);
  }, []);
  const saveCustom = (emoji: string, text: string, clearAfter: CustomStatusClearAfter) =>
    customSaver.run(
      async () => {
        const ok = await data.call('accountSetCustomStatus', emoji, text, clearAfter);
        if (ok && account) {
          const entry = { emoji, text, duration: clearAfter };
          patch({ recentCustomStatuses: [entry, ...account.recentCustomStatuses.filter((r) => r.emoji !== emoji || r.text !== text)].slice(0, 5) });
        }
        return ok;
      },
      undefined,
      t('profile.customStatus.failed'),
    );
  const clearCustom = () => customSaver.run(() => data.call('accountClearCustomStatus'), undefined, t('profile.customStatus.failed'));

  // MARK: out of office
  const ooo = useSaver();
  const [oooEditing, setOooEditing] = useState(false);
  const responder = account?.autoResponder ?? null;
  const setResponder = (active: boolean, message: string) => {
    const before = responder;
    patch({ autoResponder: { active, message } });
    ooo.run(
      () => data.call('accountSetAutoResponder', active, message),
      () => before && patch({ autoResponder: before }),
      t('profile.ooo.failed'),
    );
  };

  // MARK: presence pin (system administrators)
  const [pin, setPin] = useState<PresenceOverride | null>(null);
  const pinSaver = useSaver();
  useEffect(() => {
    if (!account?.isSystemAdmin) return;
    let alive = true;
    data.call('accountPresenceOverride').then((v) => alive && setPin(v), () => undefined);
    return () => {
      alive = false;
    };
  }, [account?.isSystemAdmin]);
  const setPinValue = (value: PresenceOverride) => {
    const before = pin;
    setPin(value);
    pinSaver.run(
      async () => {
        const answer = await data.call('accountSetPresenceOverride', value);
        if (answer) setPin(answer);
        return !!answer;
      },
      () => setPin(before),
      t('profile.presence.failed'),
    );
  };

  const oooPreview = responder?.message || t('profile.ooo.messagePlaceholder');

  return (
    <>
      <SettingsGroup title={t('profile.section.status')} save={presence.state}>
        <div role="radiogroup" aria-label={t('profile.section.status')}>
          {SELECTABLE.map((value) => (
            <RadioRow
              key={value}
              leading={<PresenceDot status={value} />}
              title={statusLabel(value)}
              subtitle={value === 'dnd' && status === 'dnd' && dndEnd > 0 ? dndUntilText(dndEnd, Date.now(), hour12) ?? undefined : undefined}
              checked={status === value}
              onPick={(e) => {
                if (value === 'dnd') setDndAnchor(e.currentTarget.getBoundingClientRect());
                else setStatus(value);
              }}
            />
          ))}
          {status === 'ooo' ? <RadioRow leading={<PresenceDot status="ooo" />} title={statusLabel('ooo')} checked onPick={() => setOooEditing(true)} /> : null}
        </div>
      </SettingsGroup>
      {dndAnchor ? (
        <DndDurationMenu
          anchor={dndAnchor}
          onClose={() => setDndAnchor(null)}
          onPick={(d) => {
            setDndAnchor(null);
            setStatus('dnd', dndEndTime(d, Date.now()));
          }}
          onCustom={() => {
            setDndAnchor(null);
            setDndCustom(true);
          }}
        />
      ) : null}
      {dndCustom ? (
        <DndCustomDialog
          onClose={() => setDndCustom(false)}
          onPick={(end) => {
            setDndCustom(false);
            setStatus('dnd', end);
          }}
        />
      ) : null}

      <SettingsGroup title={t('profile.customStatus.section')} save={customSaver.state}>
        <button type="button" className="list-row clickable st-custom-row" onClick={() => setEditing(true)}>
          <span className="list-row-leading st-emoji">{custom ? <EmojiGlyph name={custom.emoji} /> : fallbackCharacter}</span>
          <span className="list-row-text">
            <span className={`list-row-title${custom ? '' : ' st-muted'}`}>{custom ? custom.text : t('profile.customStatus.set')}</span>
            {custom?.expiresAt ? <span className="list-row-subtitle">{tf('profile.customStatus.until', formatExpiry(custom.expiresAt, Date.now(), locale(), undefined, hour12))}</span> : null}
          </span>
          <Icon name="chevronRight" size={15} />
        </button>
        {custom ? (
          <button type="button" className="list-row clickable danger" onClick={clearCustom}>
            <span className="list-row-text">
              <span className="list-row-title">{t('profile.customStatus.clear')}</span>
            </span>
          </button>
        ) : null}
      </SettingsGroup>

      <SettingsGroup title={t('profile.ooo.section')} save={ooo.state} footer={t('profile.ooo.explainer')}>
        <SwitchRow
          title={t('profile.ooo.toggle')}
          on={responder?.active === true}
          disabled={!responder}
          onChange={(on) => {
            const message = responder?.message ?? '';
            // Nothing to auto-reply WITH yet: writing the message IS turning it on.
            if (on && !message.trim()) setOooEditing(true);
            else setResponder(on, message);
          }}
        />
        <button type="button" className="list-row clickable" onClick={() => setOooEditing(true)} disabled={!responder}>
          <span className="list-row-text">
            <span className="list-row-title">{t('profile.ooo.editMessage')}</span>
          </span>
          <span className={`list-row-trailing st-ooo-preview${responder?.message ? '' : ' st-muted'}`}>{oooPreview}</span>
          <Icon name="chevronRight" size={15} />
        </button>
      </SettingsGroup>

      {account?.isSystemAdmin && pin ? (
        <SettingsGroup title={t('profile.presence.section')} save={pinSaver.state} footer={t('profile.presence.explainer')}>
          <SwitchRow title={t('profile.presence.alwaysOnline')} on={pin.alwaysOnline} onChange={(on) => setPinValue(PresenceOverrides.withAlwaysOnline(pin, on))} />
          <SwitchRow title={t('profile.presence.alwaysDesktop')} on={pin.alwaysDesktop} onChange={(on) => setPinValue(PresenceOverrides.withAlwaysDesktop(pin, on))} />
        </SettingsGroup>
      ) : null}

      {editing ? (
        <CustomStatusDialog
          current={custom}
          recents={account?.recentCustomStatuses ?? []}
          hour12={hour12}
          onForget={(r) => {
            if (!account) return;
            patch({ recentCustomStatuses: account.recentCustomStatuses.filter((x) => x !== r) });
            void data.call('accountForgetRecentStatus', r);
          }}
          onClear={() => {
            setEditing(false);
            clearCustom();
          }}
          onSave={(emoji, text, clearAfter) => {
            setEditing(false);
            saveCustom(emoji, text, clearAfter);
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {oooEditing ? (
        <OutOfOfficeDialog
          current={responder?.message ?? ''}
          onSave={(message) => {
            setOooEditing(false);
            setResponder(true, message);
          }}
          onClose={() => setOooEditing(false)}
        />
      ) : null}
    </>
  );
}

/** Emoji, sentence, and the six classic "clear after" choices (CustomStatusEditorSheet). */
export function CustomStatusDialog({
  current,
  recents,
  hour12,
  onSave,
  onClear,
  onForget,
  onClose,
}: {
  current: CustomStatus | null;
  recents: RecentCustomStatus[];
  hour12?: boolean;
  onSave(emoji: string, text: string, clearAfter: CustomStatusClearAfter): void;
  onClear(): void;
  onForget(r: RecentCustomStatus): void;
  onClose(): void;
}) {
  const [emoji, setEmoji] = useState(current?.emoji || 'speech_balloon');
  const [text, setText] = useState(current?.text ?? '');
  const [clearAfter, setClearAfter] = useState<CustomStatusClearAfter>(CustomStatusClearAfter.today);
  const [picker, setPicker] = useState<DOMRect | null>(null);
  const presets = [
    { emoji: 'palm_tree', text: t('profile.preset.outOfOffice') },
    { emoji: 'calendar', text: t('profile.preset.inAMeeting') },
    { emoji: 'house', text: t('profile.preset.workingFromHome') },
    { emoji: 'face_with_thermometer', text: t('profile.preset.outSick') },
    { emoji: 'fork_and_knife', text: t('profile.preset.outForLunch') },
  ];
  const trimmed = text.trim();
  const preview = CustomStatusDurations.expiresAt(clearAfter, Date.now());
  const isClearAfter = (v: string): v is CustomStatusClearAfter => (customStatusClearAfterAllCases as readonly string[]).includes(v);
  return (
    <Dialog
      title={t('profile.customStatus.section')}
      onClose={onClose}
      width={460}
      footer={
        <>
          {current ? (
            <Button kind="danger" onClick={onClear}>
              {t('profile.customStatus.clear')}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" disabled={!trimmed} onClick={() => onSave(emoji, trimmed, clearAfter)}>
            {t('profile.customStatus.save')}
          </Button>
        </>
      }
    >
      <div className="st-cs-input">
        <button type="button" className="st-cs-emoji" onClick={(e) => setPicker(e.currentTarget.getBoundingClientRect())} title={t('web.settings.customStatus.pickEmoji')} aria-label={t('web.settings.customStatus.pickEmoji')}>
          <EmojiGlyph name={emoji} />
        </button>
        <input
          className="text-input"
          value={text}
          maxLength={100}
          placeholder={t('profile.customStatus.placeholder')}
          onChange={(e) => setText(e.target.value.slice(0, 100))}
          onKeyDown={(e) => e.key === 'Enter' && trimmed && onSave(emoji, trimmed, clearAfter)}
        />
        {text ? (
          <button type="button" className="icon-button st-cs-clear" onClick={() => setText('')} aria-label={t('profile.customStatus.clear')}>
            <Icon name="x" size={14} />
          </button>
        ) : null}
      </div>

      {recents.length ? (
        <SettingsGroup title={t('web.settings.customStatus.recent')}>
          {recents.map((r) => (
            <div key={`${r.emoji}|${r.text}`} className="st-preset-row">
              <button
                type="button"
                className="list-row clickable"
                onClick={() => {
                  setEmoji(r.emoji || 'speech_balloon');
                  setText(r.text);
                  if (isClearAfter(r.duration)) setClearAfter(r.duration);
                }}
              >
                <span className="list-row-leading st-emoji">
                  <EmojiGlyph name={r.emoji || 'speech_balloon'} />
                </span>
                <span className="list-row-text">
                  <span className="list-row-title">{r.text}</span>
                </span>
              </button>
              <button type="button" className="icon-button st-preset-forget" onClick={() => onForget(r)} title={t('web.settings.customStatus.forget')} aria-label={t('web.settings.customStatus.forget')}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
        </SettingsGroup>
      ) : null}

      <SettingsGroup title={t('profile.customStatus.suggestions')}>
        {presets.map((p) => (
          <button
            key={p.emoji}
            type="button"
            className="list-row clickable"
            onClick={() => {
              setEmoji(p.emoji);
              setText(p.text);
            }}
          >
            <span className="list-row-leading st-emoji">
              <EmojiGlyph name={p.emoji} />
            </span>
            <span className="list-row-text">
              <span className="list-row-title">{p.text}</span>
            </span>
          </button>
        ))}
      </SettingsGroup>

      <SettingsGroup title={t('profile.customStatus.clearAfter')} footer={preview != null ? tf('profile.customStatus.until', formatExpiry(preview, Date.now(), locale(), undefined, hour12)) : undefined}>
        <div role="radiogroup">
          {customStatusClearAfterAllCases.map((o) => (
            <RadioRow key={o || 'none'} title={clearAfterLabel(o)} checked={clearAfter === o} onPick={() => setClearAfter(o)} />
          ))}
        </div>
      </SettingsGroup>

      {picker ? (
        <EmojiPicker
          anchor={picker}
          title={t('web.settings.customStatus.pickEmoji')}
          prefer="below"
          onPick={(name) => {
            setEmoji(name);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      ) : null}
    </Dialog>
  );
}

/** The out-of-office reply's editor: one honest multiline field, and a save that activates it. */
export function OutOfOfficeDialog({ current, onSave, onClose }: { current: string; onSave(message: string): void; onClose(): void }) {
  const [message, setMessage] = useState(current);
  const trimmed = message.trim();
  return (
    <Dialog
      title={t('profile.ooo.section')}
      onClose={onClose}
      width={460}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" disabled={!trimmed} onClick={() => onSave(trimmed)}>
            {t('profile.ooo.save')}
          </Button>
        </>
      }
    >
      <textarea className="text-input st-ooo-text" rows={5} maxLength={500} value={message} placeholder={t('profile.ooo.messagePlaceholder')} onChange={(e) => setMessage(e.target.value.slice(0, 500))} />
      <p className="st-footer">{t('profile.ooo.explainer')}</p>
    </Dialog>
  );
}
